import { useState, useRef } from 'react';

interface MusicPlayerProps {
  title: string;
  context: string;
  duration: string;
  imageUrl: string;
}

interface FileItem {
  name: string;
  size: string;
  type: 'audio' | 'pdf' | 'ppt';
  audioUrl?: string;
}

export default function MusicPlayer({ title, context, duration, imageUrl }: MusicPlayerProps) {
  const [showContents, setShowContents] = useState(false);
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [selectedFile, setSelectedFile] = useState<string>('');
  const [formData, setFormData] = useState({ name: '', email: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [playingAudio, setPlayingAudio] = useState<string | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<string[]>([]);
  const [userName, setUserName] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [showNotification, setShowNotification] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const files: FileItem[] = [
    { 
      name: `${title} - Full Version.mp3`, 
      size: '8.5 MB', 
      type: 'audio',
      audioUrl: 'https://public.readdy.ai/ai/img_res/5c9bdba01415fe0ef622a175ca75ee78.jpg'
    },
    { 
      name: `${title} - Instrumental.mp3`, 
      size: '7.2 MB', 
      type: 'audio',
      audioUrl: 'https://readdy.ai/api/search-image?query=sample-worship-music-instrumental-version-peaceful-contemporary-christian-background-music-with-soft-piano-strings-and-acoustic-guitar-perfect-for-meditation&width=100&height=100&seq=audio2&orientation=squarish'
    },
    { name: `${title} - Lyrics.pdf`, size: '245 KB', type: 'pdf' },
    { name: `${title} - Chord Chart.pdf`, size: '180 KB', type: 'pdf' },
    { name: `${title} - Slides.pptx`, size: '3.1 MB', type: 'ppt' },
  ];

  const handleFileClick = (fileName: string, fileType: string) => {
    // Toggle selection for all files
    setSelectedFiles(prev => {
      if (prev.includes(fileName)) {
        return prev.filter(f => f !== fileName);
      } else {
        return [...prev, fileName];
      }
    });
  };

  const handlePlayAudio = (fileName: string, audioUrl: string, e: React.MouseEvent) => {
    e.stopPropagation();
    
    if (playingAudio === fileName) {
      // Stop playing
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
      setPlayingAudio(null);
    } else {
      // Start playing
      if (audioRef.current) {
        audioRef.current.pause();
      }
      
      const audio = new Audio(audioUrl);
      audioRef.current = audio;
      audio.play();
      setPlayingAudio(fileName);
      
      audio.onended = () => {
        setPlayingAudio(null);
      };
    }
  };

  const handleDownloadSelected = () => {
    if (selectedFiles.length === 0) {
      alert('Please select at least one file to download.');
      return;
    }
    
    // Show email form for downloading selected files
    setSelectedFile(`Selected Files (${selectedFiles.length})`);
    setShowContents(false);
    setShowEmailForm(true);
  };

  const handleDownloadAll = () => {
    // Show email form for downloading all files
    setSelectedFile(`${title} - Complete Folder`);
    setShowContents(false);
    setShowEmailForm(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const formBody = new URLSearchParams({
        name: formData.name,
        email: formData.email,
        music_title: title,
        music_context: context,
        requested_file: selectedFile,
        selected_files: selectedFiles.join(', '),
      }).toString();

      const response = await fetch('https://readdy.ai/api/form/submit/music-download-form', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody,
      });

      if (response.ok) {
        // Show success message
        setShowSuccess(true);
        setFormData({ name: '', email: '' });
        setSelectedFiles([]);
        
        // Keep the success message visible for 4 seconds before closing
        setTimeout(() => {
          setShowSuccess(false);
          setShowEmailForm(false);
        }, 4000);
      } else {
        alert('Failed to send. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('An error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSendEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const response = await fetch('YOUR_FORM_SUBMISSION_URL', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          name: userName,
          email: userEmail,
          files: selectedFiles.join(', '),
        }),
      });

      if (response.ok) {
        setShowEmailForm(false);
        setShowNotification(true);
        setUserName('');
        setUserEmail('');
        setSelectedFiles([]);
        
        setTimeout(() => {
          setShowNotification(false);
        }, 4000);
      }
    } catch (error) {
      console.error('Error sending email:', error);
    }
  };

  const getFileIcon = (type: string) => {
    switch (type) {
      case 'audio':
        return <i className="ri-music-2-fill text-emerald-500"></i>;
      case 'pdf':
        return <i className="ri-file-pdf-fill text-red-500"></i>;
      case 'ppt':
        return <i className="ri-file-ppt-fill text-orange-500"></i>;
      default:
        return <i className="ri-file-fill text-gray-500"></i>;
    }
  };

  const handleModalClose = () => {
    setShowContents(false);
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    setPlayingAudio(null);
    setSelectedFiles([]);
  };

  return (
    <>
      {/* Success Notification Pop-up */}
      {showNotification && (
        <div className="fixed top-6 right-6 z-[60] animate-slide-in">
          <div className="bg-white rounded-xl shadow-2xl border-2 border-emerald-500 p-4 flex items-center gap-4 min-w-80">
            <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0">
              <i className="ri-check-line text-2xl text-emerald-600"></i>
            </div>
            <div className="flex-1">
              <h4 className="text-base font-bold text-gray-900 mb-1">Check Your Email!</h4>
              <p className="text-sm text-gray-600">
                Download link sent successfully.
              </p>
            </div>
            <button
              onClick={() => setShowNotification(false)}
              className="text-gray-400 hover:text-gray-600 cursor-pointer"
            >
              <i className="ri-close-line text-xl"></i>
            </button>
          </div>
        </div>
      )}

      {/* Email Form Modal */}
      {showEmailForm && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full">
            <div className="relative h-32 bg-gradient-to-br from-emerald-600 to-teal-700 rounded-t-2xl overflow-hidden">
              <div className="absolute inset-0 bg-[url('https://readdy.ai/api/search-image?query=abstract%20flowing%20musical%20notes%20and%20waves%20pattern%20in%20soft%20emerald%20green%20and%20white%20colors%20minimalist%20elegant%20design%20smooth%20gradient%20background%20peaceful%20worship%20atmosphere&width=800&height=400&seq=music-download-header&orientation=landscape')] opacity-20 bg-cover bg-center"></div>
              <div className="relative h-full flex items-center justify-between px-6">
                <div>
                  <h3 className="text-2xl font-bold text-white mb-1" style={{ fontFamily: 'Noto Sans, system-ui, sans-serif' }}>
                    Download Music
                  </h3>
                  <p className="text-emerald-100 text-sm">
                    Enter your details to receive the download link
                  </p>
                </div>
                <button
                  onClick={() => setShowEmailForm(false)}
                  className="text-white hover:bg-white/20 rounded-lg p-2 transition-colors cursor-pointer"
                >
                  <i className="ri-close-line text-2xl"></i>
                </button>
              </div>
            </div>

            <div className="p-6">
              <form onSubmit={handleSendEmail} className="space-y-4">
                <div className="bg-emerald-50 rounded-lg p-4 mb-4">
                  <div className="text-sm font-semibold text-emerald-800 mb-2">
                    Selected Files ({selectedFiles.length})
                  </div>
                  <div className="space-y-1">
                    {selectedFiles.map((file, index) => (
                      <div key={index} className="flex items-center gap-2 text-sm text-emerald-700">
                        <i className="ri-music-2-line"></i>
                        <span>{file}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Your Name
                  </label>
                  <input
                    type="text"
                    value={userName}
                    onChange={(e) => setUserName(e.target.value)}
                    required
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-emerald-500 focus:outline-none transition-colors text-base text-gray-900"
                    placeholder="Enter your name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Email Address
                  </label>
                  <input
                    type="email"
                    value={userEmail}
                    onChange={(e) => setUserEmail(e.target.value)}
                    required
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-emerald-500 focus:outline-none transition-colors text-base text-gray-900"
                    placeholder="your.email@example.com"
                  />
                </div>

                <button
                  type="submit"
                  className="w-full py-3 bg-emerald-600 text-white rounded-lg font-semibold hover:bg-emerald-700 transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
                >
                  <i className="ri-send-plane-fill"></i>
                  <span>Send Download Link</span>
                </button>
              </form>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm overflow-hidden hover:shadow-md transition-all duration-300 border border-gray-100">
        <div className="relative h-48 overflow-hidden">
          <img
            src={imageUrl}
            alt={title}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent"></div>
          <div className="absolute bottom-3 left-3 right-3">
            <span className="inline-block px-3 py-1 bg-emerald-500/90 text-white text-xs font-medium rounded-full mb-2">
              {context}
            </span>
            <h3 className="text-white font-semibold text-lg line-clamp-2">{title}</h3>
          </div>
        </div>

        <div className="p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2 text-gray-600 text-sm">
              <i className="ri-time-line"></i>
              <span>{duration}</span>
            </div>
            <div className="flex items-center gap-2 text-gray-600 text-sm">
              <i className="ri-folder-music-line"></i>
              <span>5 files</span>
            </div>
          </div>

          <button
            onClick={() => setShowContents(true)}
            className="w-full bg-gradient-to-r from-emerald-500 to-teal-500 text-white py-2.5 rounded-lg font-medium hover:from-emerald-600 hover:to-teal-600 transition-all duration-300 flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
          >
            <i className="ri-folder-open-line text-lg"></i>
            Open Folder
          </button>
        </div>
      </div>

      {/* Folder Contents Modal */}
      {showContents && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-gradient-to-r from-emerald-500 to-teal-500 p-6 rounded-t-2xl">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="text-2xl font-bold text-white mb-2">{title}</h3>
                  <p className="text-emerald-50 text-sm">Complete worship music package</p>
                </div>
                <button
                  onClick={handleModalClose}
                  className="text-white hover:bg-white/20 rounded-lg p-2 transition-colors cursor-pointer"
                >
                  <i className="ri-close-line text-2xl"></i>
                </button>
              </div>
            </div>

            <div className="p-6">
              <div className="mb-6">
                <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <i className="ri-folder-music-fill text-emerald-500"></i>
                  Folder Contents
                </h4>
                <div className="space-y-2">
                  {files.map((file, index) => (
                    <div
                      key={index}
                      className={`w-full flex items-center gap-3 p-4 rounded-lg transition-all cursor-pointer group border ${
                        selectedFiles.includes(file.name)
                          ? 'bg-emerald-100 border-emerald-400'
                          : 'bg-gray-50 hover:bg-emerald-50 border-transparent hover:border-emerald-200'
                      }`}
                      onClick={() => handleFileClick(file.name, file.type)}
                    >
                      {/* Checkbox for all files */}
                      <div className="flex-shrink-0">
                        <div className={`w-6 h-6 rounded border-2 flex items-center justify-center transition-all ${
                          selectedFiles.includes(file.name)
                            ? 'bg-emerald-500 border-emerald-500'
                            : 'bg-white border-gray-300 group-hover:border-emerald-400'
                        }`}>
                          {selectedFiles.includes(file.name) && (
                            <i className="ri-check-line text-white text-sm font-bold"></i>
                          )}
                        </div>
                      </div>
                      
                      <div className="w-10 h-10 flex items-center justify-center bg-white rounded-lg text-xl shadow-sm flex-shrink-0">
                        {getFileIcon(file.type)}
                      </div>
                      <div className="flex-1 text-left min-w-0">
                        <p className="text-gray-900 font-medium text-sm group-hover:text-emerald-600 transition-colors truncate">
                          {file.name}
                        </p>
                        <p className="text-gray-500 text-xs">{file.size}</p>
                      </div>
                      
                      {file.type === 'audio' && (
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <button
                            onClick={(e) => handlePlayAudio(file.name, file.audioUrl!, e)}
                            className="w-10 h-10 flex items-center justify-center bg-emerald-500 hover:bg-emerald-600 text-white rounded-full transition-colors"
                          >
                            <i className={`${playingAudio === file.name ? 'ri-stop-fill' : 'ri-play-fill'} text-lg`}></i>
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {selectedFiles.length > 0 && (
                <div className="mb-4">
                  <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <i className="ri-checkbox-circle-line text-emerald-600 text-xl flex-shrink-0 mt-0.5"></i>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-emerald-900 mb-1">
                          {selectedFiles.length} File{selectedFiles.length > 1 ? 's' : ''} Selected
                        </p>
                        <p className="text-xs text-emerald-700">
                          Click the button below to download your selected files via email.
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <button
                    onClick={handleDownloadSelected}
                    className="w-full mt-3 bg-gradient-to-r from-emerald-500 to-teal-500 text-white py-3 rounded-lg font-semibold hover:from-emerald-600 hover:to-teal-600 transition-all duration-300 flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer shadow-lg hover:shadow-xl"
                  >
                    <i className="ri-download-line text-xl"></i>
                    Download Selected Files ({selectedFiles.length})
                  </button>
                </div>
              )}

              <div className="border-t border-gray-200 pt-6">
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 mb-4">
                  <div className="flex items-start gap-3">
                    <i className="ri-information-line text-emerald-600 text-xl flex-shrink-0 mt-0.5"></i>
                    <div>
                      <p className="text-sm font-medium text-emerald-900 mb-1">
                        Download All Files
                      </p>
                      <p className="text-xs text-emerald-700">
                        Get the complete package sent to your email. Includes all 5 files in one convenient download.
                      </p>
                    </div>
                  </div>
                </div>
                
                <button
                  onClick={handleDownloadAll}
                  className="w-full bg-gradient-to-r from-emerald-500 to-teal-500 text-white py-3 rounded-lg font-semibold hover:from-emerald-600 hover:to-teal-600 transition-all duration-300 flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer shadow-lg hover:shadow-xl"
                >
                  <i className="ri-download-cloud-line text-xl"></i>
                  Download All Files (Email Required)
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Email Collection Modal */}
      {showEmailForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full">
            <div className="bg-gradient-to-r from-emerald-500 to-teal-500 p-6 rounded-t-2xl">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="text-2xl font-bold text-white mb-2">Download Package</h3>
                  <p className="text-emerald-50 text-sm">Enter your email to receive the files</p>
                </div>
                <button
                  onClick={() => setShowEmailForm(false)}
                  className="text-white hover:bg-white/20 rounded-lg p-2 transition-colors cursor-pointer"
                >
                  <i className="ri-close-line text-2xl"></i>
                </button>
              </div>
            </div>

            <div className="p-6">
              {showSuccess ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-check-line text-3xl text-emerald-500"></i>
                  </div>
                  <h4 className="text-xl font-bold text-gray-900 mb-2">Check Your Email!</h4>
                  <p className="text-gray-600">
                    We've sent the download link to your email address.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4" data-readdy-form id="music-download-form">
                  <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 mb-4">
                    <p className="text-sm text-emerald-800 font-medium flex items-center gap-2">
                      <i className="ri-folder-music-line"></i>
                      {selectedFile}
                    </p>
                    {selectedFiles.length > 0 && (
                      <div className="mt-2 space-y-1">
                        {selectedFiles.map((fileName, idx) => (
                          <p key={idx} className="text-xs text-emerald-600 ml-6">
                            • {fileName}
                          </p>
                        ))}
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Your Name
                    </label>
                    <input
                      type="text"
                      name="name"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-base text-gray-900 bg-white"
                      placeholder="Enter your name"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email Address
                    </label>
                    <input
                      type="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-base text-gray-900 bg-white"
                      placeholder="your@email.com"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-gradient-to-r from-emerald-500 to-teal-500 text-white py-3 rounded-lg font-semibold hover:from-emerald-600 hover:to-teal-600 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
                  >
                    {isSubmitting ? (
                      <>
                        <i className="ri-loader-4-line animate-spin"></i>
                        Sending...
                      </>
                    ) : (
                      <>
                        <i className="ri-mail-send-line"></i>
                        Send Download Link
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
